/** Indices of a convex polygon's corners (x, y pairs in any order), counter-clockwise about its centroid. */
export function convexPolygonOrder(
    vertexCoordinates: ArrayLike<number>
): number[] {
    const count = vertexCoordinates.length / 2;
    let cx = 0;
    let cy = 0;
    for (let i = 0; i < count; i++) {
        cx += vertexCoordinates[2 * i] / count;
        cy += vertexCoordinates[2 * i + 1] / count;
    }
    const corners = new Array<{ index: number; angle: number }>(count);
    for (let i = 0; i < count; i++) {
        const angle = Math.atan2(
            vertexCoordinates[2 * i + 1] - cy,
            vertexCoordinates[2 * i] - cx
        );
        corners[i] = { index: i, angle };
    }
    return corners.sort((a, b) => a.angle - b.angle).map(({ index }) => index);
}

/**
 * Triangulates a convex polygon whose corners are given in any order.\
 * The corners are put in order ({@link convexPolygonOrder}), and
 * triangles are formed between consecutive corners and the last one.
 * @param vertexCoordinates x, y of each corner.
 * @param startIndex Index of the first corner in the index buffer the
 *     result goes into, for polygons that share one buffer.
 * @returns Indices forming primitives of type {@link WebGL2RenderingContext.TRIANGLES}.
 *     A polygon of one or two corners gives a single degenerate triangle, an empty one gives no indices.
 */
export function tesselateConvexPolygon(
    vertexCoordinates: Float32Array,
    startIndex: number
): number[] {
    const numberOfPoints = vertexCoordinates.length / 2;
    if (numberOfPoints > 2) {
        const order = convexPolygonOrder(vertexCoordinates);
        const indices = new Array<number>((numberOfPoints - 2) * 3);
        const last = startIndex + order[numberOfPoints - 1];
        let currentIndex = 0;
        for (let a = 1, b = numberOfPoints - 1; a < b; ++a) {
            indices[currentIndex++] = startIndex + order[a - 1];
            indices[currentIndex++] = startIndex + order[a];
            indices[currentIndex++] = last;
        }

        return indices;
    } else if (numberOfPoints > 0) {
        return [
            startIndex,
            startIndex + (numberOfPoints === 2 ? 1 : 0),
            startIndex,
        ];
    }
    return [];
}
